import React from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { Video, Clock, Zap, Users } from 'lucide-react';
import { LiveKPIs, ChartTheme } from './types';
import { KPICard, KPICardGrid, BadgeKPI } from './KPICard';
import { getBaseChartOptions } from './chartUtils';
import { useTranslation } from 'react-i18next';

interface LiveSectionProps {
    data: LiveKPIs;
    theme: ChartTheme;
}

export const LiveSection: React.FC<LiveSectionProps> = ({ data, theme }) => {
    const { t } = useTranslation();
    const baseOptions = getBaseChartOptions(theme);

    // Column/Heatmap Chart - Ventes par heure
    const salesByHourOptions: Highcharts.Options = {
        ...baseOptions,
        chart: {
            ...baseOptions.chart,
            type: 'column',
            height: 300,
        },
        title: {
            ...baseOptions.title,
            text: t('admin.dashboard.analytics.live.chart.title'),
        },
        subtitle: {
            ...baseOptions.subtitle,
            text: t('admin.dashboard.analytics.live.chart.subtitle'),
        },
        xAxis: {
            ...baseOptions.xAxis,
            categories: data.salesByHour.map(d => d.hour),
            title: { text: t('admin.dashboard.analytics.live.chart.xAxis'), style: { color: theme.textColor } },
        },
        yAxis: {
            ...baseOptions.yAxis,
            title: { text: t('admin.dashboard.analytics.live.chart.yAxis'), style: { color: theme.textColor } },
            allowDecimals: false,
        },
        tooltip: {
            ...baseOptions.tooltip,
            formatter: function (this: any) {
                return `<b>${this.x}</b><br/>${t('admin.dashboard.analytics.live.chart.sales')}: ${this.y}`;
            },
        },
        plotOptions: {
            column: {
                borderRadius: 4,
                borderWidth: 0,
                colorByPoint: true,
                colors: data.salesByHour.map((d, i) => {
                    const maxCount = Math.max(...data.salesByHour.map(s => s.count));
                    const intensity = maxCount > 0 ? d.count / maxCount : 0;
                    // Gradient from light to dark based on intensity
                    if (intensity > 0.8) return theme.successColor;
                    if (intensity > 0.5) return theme.primaryColor;
                    if (intensity > 0.2) return theme.warningColor;
                    return Highcharts.color(theme.primaryColor).setOpacity(0.3).get('rgba') as string;
                }),
            },
        },
        legend: { enabled: false },
        series: [{
            name: t('admin.dashboard.analytics.live.chart.sales'),
            type: 'column',
            data: data.salesByHour.map(d => d.count),
        }],
    };

    return (
        <section className="space-y-6">
            <div className="flex items-center gap-2 mb-4">
                <div className="p-2 bg-error-10 rounded-lg">
                    <Video className="h-5 w-5 text-error" />
                </div>
                <h2 className="text-xl font-bold text-foreground">{t('admin.dashboard.analytics.live.title')}</h2>
                <span className="text-xs text-foreground-secondary bg-warning-10 px-2 py-1 rounded-full ml-2">
                    ⚠️ {t('admin.dashboard.analytics.live.tag')}
                </span>
            </div>

            {/* Note d'information */}
            <div className="bg-info-5 border border-info-20 rounded-lg p-4 text-sm text-foreground">
                <p className="flex items-center gap-2">
                    <span className="text-info">ℹ️</span>
                    {t('admin.dashboard.analytics.live.info')}
                </p>
            </div>

            {/* KPI Cards */}
            <KPICardGrid columns={3}>
                <KPICard
                    title={t('admin.dashboard.analytics.live.kpis.peakHour')}
                    value={data.peakHour}
                    icon={<Clock className="h-5 w-5 text-warning" />}
                    color="warning"
                    tooltip={t('admin.dashboard.analytics.live.kpis.peakHourTooltip')}
                />
                <KPICard
                    title={t('admin.dashboard.analytics.live.kpis.peakSales')}
                    value={`${data.peakSalesCount} ${t('admin.dashboard.analytics.live.chart.sales')}`}
                    icon={<Zap className="h-5 w-5 text-success" />}
                    color="success"
                    tooltip={t('admin.dashboard.analytics.live.kpis.peakSalesTooltip')}
                />
                <KPICard
                    title={t('admin.dashboard.analytics.live.kpis.multiPurchase')}
                    value={data.multiPurchaseCustomers}
                    format="number"
                    icon={<Users className="h-5 w-5 text-primary" />}
                    color="primary"
                    tooltip={t('admin.dashboard.analytics.live.kpis.multiPurchaseTooltip')}
                />
            </KPICardGrid>

            {/* Multi-achat Badge */}
            {data.multiPurchaseCustomers > 0 && (
                <div className="flex items-center gap-2">
                    <BadgeKPI
                        label={t('admin.dashboard.analytics.live.badge.label')}
                        value={data.multiPurchaseCustomers}
                        total={data.multiPurchaseCustomers + 10}
                        variant="success"
                    />
                    <span className="text-sm text-foreground-secondary">
                        {t('admin.dashboard.analytics.live.badge.desc')}
                    </span>
                </div>
            )}

            {/* Sales by Hour Chart */}
            <div className="bg-surface border border-border rounded-xl p-4">
                <HighchartsReact highcharts={Highcharts} options={salesByHourOptions} />
            </div>
        </section>
    );
};
